import React, { KeyboardEvent, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import DOMPurify from 'dompurify';

import ImageViewer from 'src/components/common/image-viewer';
import ImageWithFallback from 'src/components/common/image-with-fallback';
import ItemCounter from './components/item-counter';
import noImage from 'src/assets/images/no-image.svg';

import popupStore from 'src/stores/popup-store';

import { ALLOWED_TAGS, Key, LOCALES, PopupName } from 'src/constants';
import { formatMoney, getLocaleUnit, sortPhotos } from 'src/utils';
import type { CatalogItemProps } from './catalog-item.props';

import './catalog-item.scss';

const CatalogItem = ({
  addedToCart,
  item: { name, description, regularPrice, hasDiscount, price, unit, maxAmount, photos = [] },
  decrementItem,
  incrementItem,
}: CatalogItemProps) => {
  const [errorLoadingImage, setErrorLoadingImage] = useState<boolean>(false);

  const sortedPhotos = useMemo(() => sortPhotos(photos), [photos]);
  const coverPhotoSrc = sortedPhotos.length ? sortedPhotos[0].thumbUrl : '';

  const thumbnailClasses = classNames('catalog-item__thumbnail', {
    'catalog-item__thumbnail_no-image': errorLoadingImage,
  });

  const handlePopupGalleryClick = () => {
    popupStore.showPopup(PopupName.GALLERY, {
      images: sortedPhotos,
      imagesAlt: name,
      initialIndex: 0,
    });
  };

  const handleKeyUp = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === Key.ENTER) {
      event.currentTarget.click();
    }
  }, []);

  return (
    <div className="catalog-item">
      <div
        className={thumbnailClasses}
        tabIndex={errorLoadingImage ? -1 : 0}
        onClick={() => !errorLoadingImage && handlePopupGalleryClick()}
        onKeyUp={handleKeyUp}
      >
        {!!sortedPhotos.length ? (
          <ImageViewer
            images={sortedPhotos}
            imagesAlt={name}
            initialIndex={0}
            isThumbnailView
            setErrorLoadingImage={setErrorLoadingImage}
          />
        ) : (
          <ImageWithFallback
            className="catalog-item__thumbnail-image"
            alt={name}
            fallbackSrc={noImage}
            setErrorLoadingImage={setErrorLoadingImage}
            src={coverPhotoSrc}
            height={240}
            width={240}
          />
        )}

        {!errorLoadingImage && (
          <span className="catalog-item__thumbnail-label">
            <span className="catalog-item__thumbnail-label-text">{LOCALES.SEE_EXAMPLES}</span>
          </span>
        )}
      </div>
      <div className="catalog-item__content">
        <div className="catalog-item__info">
          <div className="catalog-item__heading">
            <h3 className="catalog-item__name">{name}</h3>
            <div
              className="catalog-item__description"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(description, {
                  ALLOWED_TAGS,
                  ALLOWED_ATTR: [],
                  ALLOW_ARIA_ATTR: false,
                  ALLOW_DATA_ATTR: false,
                }),
              }}
            />
          </div>
          <div
            className={classNames(
              'catalog-item__cost',
              hasDiscount && 'catalog-item__cost_discount'
            )}
          >
            <span className="catalog-item__price">
              {formatMoney(hasDiscount ? regularPrice : price)}
            </span>
            {hasDiscount && (
              <span className="catalog-item__discount-price">{formatMoney(price)}</span>
            )}
            {unit && <span className="catalog-item__unit">{getLocaleUnit(unit)}</span>}
          </div>
        </div>
        <ItemCounter
          count={addedToCart}
          maxCount={maxAmount}
          price={price}
          decrement={decrementItem}
          increment={incrementItem}
        />
      </div>
    </div>
  );
};

export default CatalogItem;
